/**
 * Company Search — "Signals at this company" (sealed element e, annotation 1).
 *
 * Rows via the get_signal_feed organization filter (G5). Each row leads with
 * its SCF score, carries an honest claim-state chip and — only when the row is
 * genuinely unclaimed — an inline Claim (server-authorized via claim_signal),
 * plus an optional secondary "New" recency chip. Claim state is honest to the
 * backend's three-state ownership model: a row assigned to another user is
 * shown as Assigned and offers no Claim, so no affordance is presented that the
 * server would reject. "Soon" is not a state and is never shown.
 *
 * Each row owns its own claim mutation so per-row pending/error is correct
 * under concurrent claims, and the button stays disabled through the
 * post-success invalidation window (no duplicate write, no swallowed error).
 */

import { useNavigate } from "react-router-dom";
import { useLinkedSignals, useClaimCompanySignal } from "../hooks";
import { isRecentlyObserved } from "../logic";
import type { LinkedSignalView } from "../types";
import { IconSignal } from "./icons";

function shortDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function SignalRow({
  signal,
  organizationId,
  now,
}: {
  signal: LinkedSignalView;
  organizationId: string;
  now: number;
}): JSX.Element {
  const navigate = useNavigate();
  const claim = useClaimCompanySignal(organizationId);
  const isNew = isRecentlyObserved(signal.observedAt, now);
  // Disabled through the post-success window too: once claimed, the button is
  // held until the invalidation refetch flips the row to its "Claimed" chip.
  const busy = claim.isPending || claim.isSuccess;

  return (
    <li className="sxco-signal">
      <span className="sxco-signal-score" aria-hidden={signal.score == null}>
        {signal.score != null ? Math.round(signal.score) : "—"}
      </span>
      <span className="sxco-signal-main">
        <button
          type="button"
          className="sxco-signal-open"
          onClick={() => navigate(`/signals/${signal.id}`)}
          aria-label={`Open signal: ${signal.title}`}
        >
          <span className="sxco-signal-title">{signal.title}</span>
          <span className="sxco-signal-meta">
            <span className="sxco-signal-icon" aria-hidden="true">
              <IconSignal />
            </span>
            {signal.signalType ?? "—"}
            {signal.tier != null && <span className="sxco-signal-tier">T{signal.tier}</span>}
            <span className="sxco-signal-date">{shortDate(signal.observedAt)}</span>
            {isNew && <span className="sxco-chip sxco-chip--sm sxco-chip--new">New</span>}
          </span>
        </button>
        {claim.isError && (
          <span className="sxco-signal-claimerr">
            {claim.error instanceof Error ? claim.error.message : "Claim failed."}
          </span>
        )}
      </span>
      <span className="sxco-signal-claim">
        {signal.claimState === "claimed" ? (
          <span className="sxco-chip sxco-chip--sm sxco-chip--muted">
            {signal.claimedByName ? `Claimed · ${signal.claimedByName}` : "Claimed"}
          </span>
        ) : signal.claimState === "assigned" ? (
          <span className="sxco-chip sxco-chip--sm sxco-chip--muted">
            {signal.assignedToName ? `Assigned · ${signal.assignedToName}` : "Assigned"}
          </span>
        ) : (
          <button
            type="button"
            className="sx-btn sx-btn-hero sxco-claim-btn"
            onClick={() => claim.mutate(signal.id)}
            disabled={busy}
          >
            {claim.isPending ? "Claiming…" : claim.isSuccess ? "Claimed" : "Claim"}
          </button>
        )}
      </span>
    </li>
  );
}

export function LinkedSignals({ organizationId }: { organizationId: string }): JSX.Element {
  const { data, isPending, isError, error } = useLinkedSignals(organizationId);
  const now = Date.now();

  return (
    <section className="sxco-section">
      <p className="sxco-card-head">
        Signals at this company
        {data && data.length > 0 && <span className="sxco-count">{data.length}</span>}
      </p>

      {isPending && <p className="sxco-muted">Loading signals…</p>}

      {isError && (
        <p className="sxco-error">
          {error instanceof Error ? error.message : "Failed to load signals."}
        </p>
      )}

      {!isPending && !isError && data && data.length === 0 && (
        <p className="sxco-muted">No signals on record for this company.</p>
      )}

      {!isPending && !isError && data && data.length > 0 && (
        <ul className="sxco-signals">
          {data.map((s) => (
            <SignalRow key={s.id} signal={s} organizationId={organizationId} now={now} />
          ))}
        </ul>
      )}
    </section>
  );
}
